import React, { createContext, useContext, useState } from 'react';
import usePage from '@dashboard/hooks/usePage';
import { confirm } from '@dashboard/lib/confirm';
import { useTicketGroups } from './TicketGroupsContext';
import { useMutation } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

const TicketGroupEditContext = createContext();

export function TicketGroupEditProvider({ children, initialIsAddingTicketType = false }) {
  const { token, sellerId } = useAuth();
  const { page, updatePage, refetch: refetchPage } = usePage();
  const { state: ticketGroups, dispatch } = useTicketGroups();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isAddingTicketGroup, setIsAddingTicketGroup] = useState(false);
  const [editingTicketGroupId, setEditingTicketGroupId] = useState(null);
  const [isAddingTicketType, setIsAddingTicketType] = useState(initialIsAddingTicketType);
  const [editingTicketTypeId, setEditingTicketTypeId] = useState(null);
  const [editingTicketGroupMode, setEditingTicketGroupMode] = useState('tickets'); // 'tickets' | 'settings' | 'layout'

  const editTicketGroupMutation = useMutation({
    mutationFn: ({ ticketGroupId, values }) =>
      fetcher(`/sellers/${sellerId}/ticket-groups/${ticketGroupId}`, token, {
        method: 'POST',
        body: JSON.stringify(values),
      }),
  });

  async function onReorderTicketTypes(ticketGroupId, items) {
    const ticketGroup = ticketGroups.find(t => t._id === ticketGroupId);
    const originalTicketTypes = [...ticketGroup.ticketTypes];

    const ticketTypeIds = items.map(t => t._id);

    // Create a new ticketGroup object to ensure reference change
    const updatedTicketGroup = {
      ...ticketGroup,
      ticketTypes: ticketTypeIds.map(id => ticketGroup.ticketTypes.find(t => t._id === id)),
    };

    const updatedTicketGroups = ticketGroups.map(tg =>
      tg._id === ticketGroupId ? updatedTicketGroup : tg,
    );

    // Create a new page object to ensure reference change
    const updatedPage = {
      ...page,
      ticketGroups: updatedTicketGroups,
    };

    // Dispatch the updated page object
    dispatch({
      type: 'set',
      values: updatedPage.ticketGroups,
    });

    try {
      await editTicketGroupMutation.mutateAsync({
        ticketGroupId: ticketGroup._id,
        values: updatedTicketGroup,
      });

      refetchPage();
    } catch (error) {
      dispatch({
        type: 'update',
        ticketGroupId,
        values: {
          ...ticketGroup,
          ticketTypes: originalTicketTypes,
        },
      });

      refetchPage();
    }
  }

  async function onReorderTicketGroups(items) {
    const initialTicketGroups = [...ticketGroups];

    dispatch({ type: 'set', values: items });

    const { data, error } = await updatePage.mutateAsync({ ticketGroups: items });

    if (!data || error) {
      dispatch({ type: 'set', values: initialTicketGroups });
    } else {
      refetchPage();
    }
  }

  async function setIsEditingTicketGroup(isEditing, ticketGroupId, mode = 'tickets') {
    let proceed = true;
    // if not editing and has unsaved changes, ask for confirmation before closing
    if (hasUnsavedChanges) {
      proceed = await confirm('Discard unsaved changes?', {
        confirm: 'Discard',
      });
    }

    if (!proceed) return;

    setHasUnsavedChanges(false);
    setIsAddingTicketType(false);
    setEditingTicketTypeId(null);

    if (isEditing) {
      setEditingTicketGroupMode(mode);
      setEditingTicketGroupId(ticketGroupId);
    } else {
      setEditingTicketGroupId(null);
      setEditingTicketGroupMode('tickets');
    }
  }

  async function onAddTicketGroup() {
    let proceed = true;

    if (hasUnsavedChanges) {
      proceed = await confirm('Discard unsaved changes?', {
        confirm: 'Discard',
      });
    }

    if (!proceed) return;

    setHasUnsavedChanges(false);
    setEditingTicketGroupMode('tickets');
    setIsAddingTicketGroup(true);
  }

  const isEditingTicketGroup = editingTicketGroupId !== null;
  const isEditingTicketType = editingTicketTypeId !== null;

  return (
    <TicketGroupEditContext.Provider
      value={{
        onAddTicketGroup,
        onReorderTicketGroups,
        setIsEditingTicketGroup,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        isAddingTicketGroup,
        setIsAddingTicketGroup,
        isEditingTicketGroup,
        editingTicketGroupId,
        setEditingTicketGroupId,
        isAddingTicketType,
        setIsAddingTicketType,
        isEditingTicketType,
        setEditingTicketTypeId,
        editingTicketTypeId,
        onReorderTicketTypes,
        editingTicketGroupMode,
        setEditingTicketGroupMode,
      }}
    >
      {children}
    </TicketGroupEditContext.Provider>
  );
}

export const useTicketGroupEdit = () => useContext(TicketGroupEditContext);
